// MACHINERY V2 (Stroje) — the picker's catalog typeahead (M2.7).
//
// Spec: data package §10 row `[HUB] Catalog search bar` — "Selecting SWITCHES
// to catalog mode". Reference impl: components/CatalogModelSearch.tsx (the
// 2-character floor, the 300 ms debounce, pageSize 20, the arrow/enter/escape
// keys and the row subtitle are all taken from it). Artboards 0800 / 0801.
//
// SAME THUNK, SAME SLICE, SAME QUERY: `searchCatalogModels({ search, pageSize:
// 20 })` into `machineCatalog.searchResults` (machineCatalogSlice.ts:5-21,
// :105-108). No new endpoint, no new state — §11's "the v2 frontend adds no new
// endpoints" holds for this sub-phase too.
//
// THE COUNT CHIP READS THE CATALOG, NOT A CONSTANT. Artboard 0801 draws
// "6 044 modelov" beside the field. Idle, that is the whole catalog — summed
// from `categories[].modelCount`, the only total the client already has
// (`getCatalogCategories`, machineCatalogSlice.ts:60-65). It becomes a search's
// own `searchTotalCount` only once THAT search has LANDED — never on typing
// alone: `searchTotalCount` is written by the fulfilled reducer
// (machineCatalogSlice.ts:107) and by nothing else, so between the keystroke
// and the response it still holds the PREVIOUS query's number (0 before the
// first query ever ran). Gating on text length would therefore print
// "0 models" for the whole 300 ms + round-trip window, then the previous
// query's total on every search after the first. `landed` below is the gate:
// the request we sent matches what is typed AND it is no longer in flight.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { formatManufacturerWithHierarchy } from "../../../../shared/utils/machineUtils";
import { CatalogModelListItem } from "../../models/models";
import {
  clearSearchResults,
  getCatalogCategories,
  searchCatalogModels,
} from "../../machineCatalogSlice";
import { SearchGlyph } from "./addAtoms";

/** v1's two floors, restated: CatalogModelSearch.tsx:44 (length) and :65-67 (ms). */
const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;

export interface CatalogSearchFieldProps {
  onSelect: (model: CatalogModelListItem) => void;
}

const CatalogSearchField: React.FC<CatalogSearchFieldProps> = ({ onSelect }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");

  const { searchResults, searchTotalCount, searchLoading, categories } = useAppSelector(
    (s) => s.machineCatalog
  );

  const [text, setText] = useState("");
  const [openList, setOpenList] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  /** The query actually DISPATCHED — not the query typed. See the chip note. */
  const [sentQuery, setSentQuery] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searching = text.trim().length >= MIN_QUERY;
  const catalogTotal = categories.reduce((sum, c) => sum + (c.modelCount || 0), 0);
  /** Results in the slice belong to `sentQuery`; `searchLoading` is true from
   *  pending to fulfilled/rejected (machineCatalogSlice.ts:102-111). */
  const landed = searching && sentQuery === text.trim() && !searchLoading;
  const count = landed ? searchTotalCount : catalogTotal;

  useEffect(() => {
    if (categories.length === 0) dispatch(getCatalogCategories());
  }, [categories.length, dispatch]);

  // The results live in a SHARED slice slot; leaving them behind would show the
  // last search's rows the next time any surface opens this field.
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      dispatch(clearSearchResults());
    },
    [dispatch]
  );

  const onChange = useCallback(
    (value: string) => {
      setText(value);
      setHighlighted(-1);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSentQuery("");
      if (value.trim().length < MIN_QUERY) {
        dispatch(clearSearchResults());
        setOpenList(false);
        return;
      }
      debounceRef.current = setTimeout(() => {
        const query = value.trim();
        dispatch(searchCatalogModels({ search: query, pageSize: 20 }));
        setSentQuery(query);
        setOpenList(true);
      }, DEBOUNCE_MS);
    },
    [dispatch]
  );

  const pick = useCallback(
    (model: CatalogModelListItem) => {
      setText("");
      setOpenList(false);
      setHighlighted(-1);
      setSentQuery("");
      dispatch(clearSearchResults());
      onSelect(model);
    },
    [dispatch, onSelect]
  );

  /** The dropdown is on screen exactly when BOTH hold — same condition as the
   *  render below, so "the list is open" means one thing in both places. */
  const listVisible = openList && searching;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!listVisible) return;
    if (e.key === "Escape") {
      // Swallowed so the dialog stays open — esc closes the LIST first.
      // >>> HANDLED BEFORE THE EMPTY-RESULTS GUARD, DELIBERATELY. <<< The list
      // is on screen with the "no model matches" row when searchResults is
      // empty; returning early there would let esc bubble to the Radix Dialog
      // (TabDialog.tsx `Dialog.Content`) and close the WHOLE add dialog.
      e.stopPropagation();
      setOpenList(false);
      return;
    }
    if (searchResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((p) => (p < searchResults.length - 1 ? p + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((p) => (p > 0 ? p - 1 : searchResults.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const model = searchResults[highlighted >= 0 ? highlighted : 0];
      if (model) pick(model);
    }
  };

  const subtitle = (m: CatalogModelListItem): string =>
    [
      m.manufacturer
        ? formatManufacturerWithHierarchy(m.manufacturer.name, m.manufacturer.parentManufacturerName)
        : "",
      m.categoryCode || "",
      m.numAxes ? `${m.numAxes} ${translate("myMachinery:axes")}` : "",
    ]
      .filter((part) => !!part)
      .join(" · ");

  return (
    <div style={{ position: "relative", minWidth: 0 }}>
      <div
        style={{
          height: 40,
          padding: "0 12px",
          borderRadius: 8,
          background: "var(--bg-soft)",
          border: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          gap: 9,
          color: "var(--faint)",
          minWidth: 0,
        }}
      >
        <SearchGlyph />
        <input
          value={text}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => {
            if (searchResults.length > 0) setOpenList(true);
          }}
          placeholder={translate("myMachinery:v2.add.searchPlaceholder")}
          aria-label={translate("myMachinery:v2.add.searchPlaceholder")}
          style={{
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: "none",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: 13.5,
            color: "var(--ink)",
          }}
        />
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--muted)",
            border: "1px solid var(--border)",
            borderRadius: 5,
            padding: "2px 6px",
            background: "var(--card)",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {translate("myMachinery:v2.add.modelCount", { count })}
        </span>
      </div>

      {listVisible && (
        <div
          className="ms-menu"
          role="listbox"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "100%",
            marginTop: 6,
            padding: 4,
            maxHeight: 260,
            overflowY: "auto",
            zIndex: 5,
          }}
        >
          {searchLoading && searchResults.length === 0 && (
            <div style={{ padding: "10px 10px", fontSize: 12.5, color: "var(--muted)" }}>
              {translate("myMachinery:v2.add.searching")}
            </div>
          )}
          {!searchLoading && searchResults.length === 0 && (
            <div style={{ padding: "10px 10px", fontSize: 12.5, color: "var(--muted)" }}>
              {translate("myMachinery:v2.add.searchNoResults")}
            </div>
          )}
          {searchResults.map((m, i) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={i === highlighted}
              className="mi"
              onMouseEnter={() => setHighlighted(i)}
              onClick={() => pick(m)}
              style={{
                width: "100%",
                textAlign: "left",
                display: "block",
                background: i === highlighted ? "var(--card-2)" : "transparent",
              }}
            >
              <span
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--ink)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {m.modelName}
              </span>
              <span
                style={{
                  display: "block",
                  fontSize: 11,
                  color: "var(--muted)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subtitle(m)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CatalogSearchField;
